import { useEffect, useMemo, useState } from "react";
import { Loader2, HandCoins, Shuffle, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { TicketGrid, TicketGridSkeleton } from "./TicketGrid";
import { normalizePhone } from "./BuyPanel";
import { api, errorMessage } from "../../lib/api";
import { nok } from "../../lib/basar";
import { useLang } from "../../context/LanguageContext";

const METHODS = [
  { value: "cash", label: "Kontant" },
  { value: "vipps_manual", label: "Vipps ved døren" },
  { value: "other", label: "Annen betaling" },
];

/**
 * Manuelt sal (kontant / Vipps ved døra / anna): kjøpar + nummer (vel sjølv i rutenett, eller auto-plukk lågaste ledige).
 * Nummera blir selde med ein gong frå same serie. `onSaved(order)` etter vellukka registrering.
 */
export const ManualSaleDialog = ({ basar, open, onOpenChange, onSaved, emailEnabled = false }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", payment_method: "cash", note: "", send_receipt: true });
  const [mode, setMode] = useState("pick");
  const [selected, setSelected] = useState(() => new Set());
  const [quantity, setQuantity] = useState("1");
  const [tickets, setTickets] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadTickets = () => api.get(`/my/basar/${basar.id}/tickets`).then((res) => setTickets(res.data)).catch(() => setTickets(null));

  useEffect(() => {
    if (!open) return;
    setForm({ name: "", phone: "", email: "", payment_method: "cash", note: "", send_receipt: true });
    setSelected(new Set());
    setQuantity(String(Math.max(1, basar.min_per_buyer || 1)));
    setTickets(null);
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, basar.id]);

  const sold = useMemo(() => new Set(tickets?.sold || []), [tickets]);
  const held = useMemo(() => new Set(tickets?.held || []), [tickets]);
  const price = Number(basar.price || 0);
  const max = Number(basar.max_per_order || 100);
  const count = mode === "pick" ? selected.size : Math.min(max, parseInt(quantity, 10) || 0);
  const numbers = useMemo(() => [...selected].sort((a, b) => a - b), [selected]);

  const toggle = (n) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n);
    else if (next.size < max) next.add(n);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      toast.error(t("Fyll inn navnet på kjøperen"));
      return;
    }
    const phone = normalizePhone(form.phone);
    if (!phone) {
      toast.error(t("Fyll inn et gyldig mobilnummer (8 siffer)"));
      return;
    }
    if (count < 1) {
      toast.error(mode === "pick" ? t("Velg minst ett nummer i rutenettet") : t("Oppgi antall lodd"));
      return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), phone, email: form.email.trim(), payment_method: form.payment_method, note: form.note.trim(), send_receipt: form.send_receipt, numbers: mode === "pick" ? numbers : [], quantity: mode === "auto" ? count : 0 };
      const { data } = await api.post(`/my/basar/${basar.id}/orders/manual`, payload);
      toast.success(t("Registrert: lodd {numbers} til {name}", { numbers: (data.order.numbers || []).join(", "), name: data.order.name }));
      onSaved?.(data.order);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "numbers_taken") {
        toast.error(t("Noen av numrene er allerede solgt eller reservert ({numbers}). Velg andre.", { numbers: (detail.numbers || []).join(", ") }));
        setSelected((prev) => { const next = new Set(prev); (detail.numbers || []).forEach((n) => next.delete(n)); return next; });
        loadTickets();
      } else {
        toast.error(errorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="basar-manual-sale-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-[hsl(var(--warning))]" /> {t("Registrer salg ved døren")}</DialogTitle>
          <DialogDescription>{t("For kontantsalg og Vipps direkte til dere. Numrene trekkes fra samme serie som nettsalget og blir solgt med en gang.")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ms-name">{t("Navn")}</Label>
              <Input id="ms-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} required autoFocus data-testid="basar-manual-sale-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ms-phone">{t("Mobilnummer")}</Label>
              <Input id="ms-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} maxLength={20} required placeholder="912 34 567" data-testid="basar-manual-sale-mobile-input" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ms-email">{t("E-post (valgfritt)")}</Label>
              <Input id="ms-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} maxLength={200} data-testid="basar-manual-sale-email-input" />
              {form.email.trim() && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1 cursor-pointer">
                  <Switch checked={form.send_receipt} onCheckedChange={(v) => set("send_receipt", v)} disabled={!emailEnabled} data-testid="basar-manual-sale-receipt-switch" />
                  <Mail className="w-3.5 h-3.5" /> {emailEnabled ? t("Send kvittering med loddnumrene på e-post") : t("E-post er ikke aktivert i denne installasjonen")}
                </label>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{t("Betaling")}</Label>
              <RadioGroup value={form.payment_method} onValueChange={(v) => set("payment_method", v)} className="flex flex-wrap gap-2" data-testid="basar-manual-sale-payment-method">
                {METHODS.map((m) => (
                  <label key={m.value} className={`inline-flex items-center gap-2 rounded-lg border px-3 h-10 text-sm cursor-pointer transition-colors duration-150 ${form.payment_method === m.value ? "border-[hsl(var(--brand))]/50 bg-[hsl(var(--brand-soft))]" : "border-border hover:bg-accent"}`}>
                    <RadioGroupItem value={m.value} data-testid={`basar-manual-sale-method-${m.value}`} /> {t(m.label)}
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>

          <Tabs value={mode} onValueChange={setMode}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <TabsList data-testid="basar-manual-sale-mode">
                <TabsTrigger value="pick" data-testid="basar-manual-sale-mode-pick">{t("Velg nummer selv")}</TabsTrigger>
                <TabsTrigger value="auto" data-testid="basar-manual-sale-mode-auto">{t("Auto-plukk")}</TabsTrigger>
              </TabsList>
              <Badge variant="outline" className="tabular-nums" data-testid="basar-manual-sale-summary">{count} {t("lodd")} · {nok(count * price)}</Badge>
            </div>
            <TabsContent value="pick" className="mt-3">
              <div className="rounded-xl border border-border p-3 max-h-[46vh] overflow-y-auto">
                {tickets ? (
                  <TicketGrid seriesFrom={tickets.series_from} seriesTo={tickets.series_to} sold={sold} held={held} winners={new Set()} selected={selected} onToggle={toggle} compact maxSelectable={max} onRefresh={loadTickets} testId="basar-manual-sale-grid" />
                ) : (
                  <TicketGridSkeleton compact />
                )}
              </div>
              {numbers.length > 0 && <p className="text-xs text-muted-foreground mt-2 tabular-nums" data-testid="basar-manual-sale-selected">{t("Valgt: {numbers}", { numbers: numbers.join(", ") })}</p>}
            </TabsContent>
            <TabsContent value="auto" className="mt-3">
              <div className="flex items-end gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ms-qty">{t("Antall lodd")}</Label>
                  <Input id="ms-qty" type="number" min={1} max={max} inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-28" data-testid="basar-manual-sale-quantity-input" />
                </div>
                <p className="text-xs text-muted-foreground pb-2.5 inline-flex items-center gap-1.5"><Shuffle className="w-3.5 h-3.5" /> {t("De laveste ledige numrene tildeles automatisk.")}</p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="space-y-1.5">
            <Label htmlFor="ms-note">{t("Merknad (internt)")}</Label>
            <Textarea id="ms-note" value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} maxLength={300} placeholder={t("F.eks. solgt av Kari på standen")} data-testid="basar-manual-sale-note-input" />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="basar-manual-sale-cancel-button">{t("Avbryt")}</Button>
            <Button type="submit" disabled={saving || count < 1} data-testid="basar-manual-sale-submit-button">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <HandCoins className="w-4 h-4 mr-1.5" />} {t("Registrer salg")}{count > 0 ? ` – ${nok(count * price)}` : ""}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
